import { useState, useEffect } from "react";
import {
  subscribeToHeaders,
  addHeader,
  updateHeader,
  deleteHeader,
  subscribeToWeeklyItems,
  addWeeklyItem,
  updateWeeklyItem,
  deleteWeeklyItem,
  toggleStarWeeklyItem
} from "../services/weeklyStatusService";

export function useWeeklyStatus(userId, weekDate) {
  const [headers, setHeaders] = useState([]);
  const [items, setItems] = useState([]);
  const [headersLoading, setHeadersLoading] = useState(true);
  const [itemsLoading, setItemsLoading] = useState(true);
  const [error, setError] = useState("");

  // 1. Subscribe to headers when userId changes
  useEffect(() => {
    if (!userId) {
      setHeaders([]);
      setHeadersLoading(false);
      return () => {};
    }

    setHeadersLoading(true);
    const unsubscribe = subscribeToHeaders(userId, (data, err) => {
      if (err) {
        setError("Failed to load weekly status headers.");
      } else {
        setHeaders(data);
        setError("");
      }
      setHeadersLoading(false);
    });

    return () => unsubscribe();
  }, [userId]);

  // 2. Subscribe to items when userId or weekDate changes
  useEffect(() => {
    if (!userId || !weekDate) {
      setItems([]);
      setItemsLoading(false);
      return () => {};
    }

    setItemsLoading(true);
    const unsubscribe = subscribeToWeeklyItems(userId, weekDate, (data, err) => {
      if (err) {
        setError("Failed to load weekly status items.");
      } else {
        setItems(data);
        setError("");
      }
      setItemsLoading(false);
    });

    return () => unsubscribe();
  }, [userId, weekDate]);

  // --- Header Handlers ---

  const handleAddHeader = async (name) => {
    try {
      setError("");
      const order = headers.length > 0 ? Math.max(...headers.map(h => h.order || 0)) + 1 : 1;
      return await addHeader(userId, name, order);
    } catch (err) {
      console.error("Error adding header:", err);
      setError(err.message || "Failed to add header.");
      throw err;
    }
  };

  const handleUpdateHeader = async (id, data) => {
    try {
      setError("");
      await updateHeader(id, data);
    } catch (err) {
      console.error("Error updating header:", err);
      setError(err.message || "Failed to update header.");
      throw err;
    }
  };

  const handleDeleteHeader = async (id) => {
    try {
      setError("");
      await deleteHeader(id);
    } catch (err) {
      console.error("Error deleting header:", err);
      setError(err.message || "Failed to delete header.");
      throw err;
    }
  };

  // --- Item Handlers ---

  const handleAddWeeklyItem = async (data) => {
    try {
      setError("");
      const itemData = {
        ...data,
        weekDate
      };
      return await addWeeklyItem(userId, itemData);
    } catch (err) {
      console.error("Error adding weekly item:", err);
      setError(err.message || "Failed to add weekly item.");
      throw err;
    }
  };

  const handleUpdateWeeklyItem = async (id, data) => {
    try {
      setError("");
      await updateWeeklyItem(id, data);
    } catch (err) {
      console.error("Error updating weekly item:", err);
      setError(err.message || "Failed to update weekly item.");
      throw err;
    }
  };

  const handleDeleteWeeklyItem = async (id) => {
    try {
      setError("");
      await deleteWeeklyItem(id);
    } catch (err) {
      console.error("Error deleting weekly item:", err);
      setError(err.message || "Failed to delete weekly item.");
      throw err;
    }
  };

  const handleToggleStarItem = async (item) => {
    try {
      setError("");
      await toggleStarWeeklyItem(userId, item);
    } catch (err) {
      console.error("Error toggling star on weekly item:", err);
      setError(err.message || "Failed to update star on item.");
      throw err;
    }
  };

  return {
    headers,
    items,
    loading: headersLoading || itemsLoading,
    error,
    setError,
    addHeader: handleAddHeader,
    updateHeader: handleUpdateHeader,
    deleteHeader: handleDeleteHeader,
    addWeeklyItem: handleAddWeeklyItem,
    updateWeeklyItem: handleUpdateWeeklyItem,
    deleteWeeklyItem: handleDeleteWeeklyItem,
    toggleStar: handleToggleStarItem
  };
}
